import { sql } from '../base';
import { Client, Goal, Matter, Project } from '../types';
import { getClientsSchemaInfo } from '../schemaCache';

// Client operations live here so we keep the main db barrel tiny and readable.
// Each helper returns plain JSON-friendly objects that React components can consume directly.
const clientsModule = {
  async findByUserId(userId: string): Promise<Client[]> {
    const result = await sql`
      SELECT * FROM clients
      WHERE user_id = ${userId}
      ORDER BY position ASC, created_at DESC
    `;
    return (result || []) as Client[];
  },

  async findById(id: string, userId: string): Promise<Client | null> {
    const result = await sql`
      SELECT * FROM clients
      WHERE id = ${id} AND user_id = ${userId}
      LIMIT 1
    `;
    return (result[0] as Client) || null;
  },

  async create(data: Omit<Client, 'id' | 'created_at' | 'updated_at'>): Promise<Client> {
    const schema = await getClientsSchemaInfo();
    let result;

    if (schema.hasNotes) {
      if (schema.hasIsArchived && schema.hasStatus) {
        result = await sql`
          INSERT INTO clients (user_id, name, email, phone, company, notes, status, position, is_archived)
          VALUES (
            ${data.user_id}, ${data.name}, ${data.email || null}, ${data.phone || null}, ${data.company || null},
            ${data.notes || null}, ${data.status || 'Active'}, ${data.position ?? 0}, ${data.is_archived ?? false}
          )
          RETURNING *
        `;
      } else if (schema.hasStatus) {
        result = await sql`
          INSERT INTO clients (user_id, name, email, phone, company, notes, status, position)
          VALUES (
            ${data.user_id}, ${data.name}, ${data.email || null}, ${data.phone || null}, ${data.company || null},
            ${data.notes || null}, ${data.status || 'Active'}, ${data.position ?? 0}
          )
          RETURNING *
        `;
      } else {
        result = await sql`
          INSERT INTO clients (user_id, name, email, phone, company, notes, position)
          VALUES (
            ${data.user_id}, ${data.name}, ${data.email || null}, ${data.phone || null}, ${data.company || null},
            ${data.notes || null}, ${data.position ?? 0}
          )
          RETURNING *
        `;
      }
    } else if (schema.hasDescription) {
      if (schema.hasIsArchived && schema.hasStatus) {
        result = await sql`
          INSERT INTO clients (user_id, name, email, phone, company, description, status, position, is_archived)
          VALUES (
            ${data.user_id}, ${data.name}, ${data.email || null}, ${data.phone || null}, ${data.company || null},
            ${data.notes || null}, ${data.status || 'Active'}, ${data.position ?? 0}, ${data.is_archived ?? false}
          )
          RETURNING *
        `;
      } else if (schema.hasStatus) {
        result = await sql`
          INSERT INTO clients (user_id, name, email, phone, company, description, status, position)
          VALUES (
            ${data.user_id}, ${data.name}, ${data.email || null}, ${data.phone || null}, ${data.company || null},
            ${data.notes || null}, ${data.status || 'Active'}, ${data.position ?? 0}
          )
          RETURNING *
        `;
      } else {
        result = await sql`
          INSERT INTO clients (user_id, name, email, phone, company, description, position)
          VALUES (
            ${data.user_id}, ${data.name}, ${data.email || null}, ${data.phone || null}, ${data.company || null},
            ${data.notes || null}, ${data.position ?? 0}
          )
          RETURNING *
        `;
      }
    } else if (schema.hasStatus) {
      if (schema.hasIsArchived) {
        result = await sql`
          INSERT INTO clients (user_id, name, email, phone, company, status, position, is_archived)
          VALUES (
            ${data.user_id}, ${data.name}, ${data.email || null}, ${data.phone || null}, ${data.company || null},
            ${data.status || 'Active'}, ${data.position ?? 0}, ${data.is_archived ?? false}
          )
          RETURNING *
        `;
      } else {
        result = await sql`
          INSERT INTO clients (user_id, name, email, phone, company, status, position)
          VALUES (
            ${data.user_id}, ${data.name}, ${data.email || null}, ${data.phone || null}, ${data.company || null},
            ${data.status || 'Active'}, ${data.position ?? 0}
          )
          RETURNING *
        `;
      }
    } else {
      result = await sql`
        INSERT INTO clients (user_id, name, email, phone, company, position)
        VALUES (
          ${data.user_id}, ${data.name}, ${data.email || null}, ${data.phone || null}, ${data.company || null},
          ${data.position ?? 0}
        )
        RETURNING *
      `;
    }

    return result[0] as Client;
  },

  async update(id: string, userId: string, data: Partial<Client>): Promise<Client> {
    const schema = await getClientsSchemaInfo();
    let result;

    if (schema.hasNotes) {
      if (schema.hasIsArchived && schema.hasStatus) {
        result = await sql`
          UPDATE clients
          SET name = COALESCE(${data.name}, name),
              email = COALESCE(${data.email}, email),
              phone = COALESCE(${data.phone}, phone),
              company = COALESCE(${data.company}, company),
              notes = COALESCE(${data.notes}, notes),
              status = COALESCE(${data.status}, status),
              position = COALESCE(${data.position}, position),
              is_archived = COALESCE(${data.is_archived}, is_archived),
              updated_at = now()
          WHERE id = ${id} AND user_id = ${userId}
          RETURNING *
        `;
      } else if (schema.hasStatus) {
        result = await sql`
          UPDATE clients
          SET name = COALESCE(${data.name}, name),
              email = COALESCE(${data.email}, email),
              phone = COALESCE(${data.phone}, phone),
              company = COALESCE(${data.company}, company),
              notes = COALESCE(${data.notes}, notes),
              status = COALESCE(${data.status}, status),
              position = COALESCE(${data.position}, position),
              updated_at = now()
          WHERE id = ${id} AND user_id = ${userId}
          RETURNING *
        `;
      } else {
        result = await sql`
          UPDATE clients
          SET name = COALESCE(${data.name}, name),
              email = COALESCE(${data.email}, email),
              phone = COALESCE(${data.phone}, phone),
              company = COALESCE(${data.company}, company),
              notes = COALESCE(${data.notes}, notes),
              position = COALESCE(${data.position}, position),
              updated_at = now()
          WHERE id = ${id} AND user_id = ${userId}
          RETURNING *
        `;
      }
    } else if (schema.hasDescription) {
      if (schema.hasStatus) {
        result = await sql`
          UPDATE clients
          SET name = COALESCE(${data.name}, name),
              email = COALESCE(${data.email}, email),
              phone = COALESCE(${data.phone}, phone),
              company = COALESCE(${data.company}, company),
              description = COALESCE(${data.notes ?? (data as any).description}, description),
              status = COALESCE(${data.status}, status),
              position = COALESCE(${data.position}, position),
              updated_at = now()
          WHERE id = ${id} AND user_id = ${userId}
          RETURNING *
        `;
      } else {
        result = await sql`
          UPDATE clients
          SET name = COALESCE(${data.name}, name),
              email = COALESCE(${data.email}, email),
              phone = COALESCE(${data.phone}, phone),
              company = COALESCE(${data.company}, company),
              description = COALESCE(${data.notes ?? (data as any).description}, description),
              position = COALESCE(${data.position}, position),
              updated_at = now()
          WHERE id = ${id} AND user_id = ${userId}
          RETURNING *
        `;
      }
    } else if (schema.hasStatus) {
      if (schema.hasIsArchived) {
        result = await sql`
          UPDATE clients
          SET name = COALESCE(${data.name}, name),
              email = COALESCE(${data.email}, email),
              phone = COALESCE(${data.phone}, phone),
              company = COALESCE(${data.company}, company),
              status = COALESCE(${data.status}, status),
              position = COALESCE(${data.position}, position),
              is_archived = COALESCE(${data.is_archived}, is_archived),
              updated_at = now()
          WHERE id = ${id} AND user_id = ${userId}
          RETURNING *
        `;
      } else {
        result = await sql`
          UPDATE clients
          SET name = COALESCE(${data.name}, name),
              email = COALESCE(${data.email}, email),
              phone = COALESCE(${data.phone}, phone),
              company = COALESCE(${data.company}, company),
              status = COALESCE(${data.status}, status),
              position = COALESCE(${data.position}, position),
              updated_at = now()
          WHERE id = ${id} AND user_id = ${userId}
          RETURNING *
        `;
      }
    } else {
      result = await sql`
        UPDATE clients
        SET name = COALESCE(${data.name}, name),
            email = COALESCE(${data.email}, email),
            phone = COALESCE(${data.phone}, phone),
            company = COALESCE(${data.company}, company),
            position = COALESCE(${data.position}, position),
            updated_at = now()
        WHERE id = ${id} AND user_id = ${userId}
        RETURNING *
      `;
    }

    return result[0] as Client;
  },

  async delete(id: string, userId: string): Promise<boolean> {
    const result = await sql`
      DELETE FROM clients
      WHERE id = ${id} AND user_id = ${userId}
    `;
    return result.count > 0;
  },

  async findProjectsByClientId(clientId: string, userId: string): Promise<Project[]> {
    const result = await sql`
      SELECT * FROM projects
      WHERE client_id = ${clientId} AND user_id = ${userId}
      ORDER BY position ASC, created_at DESC
    `;
    return (result || []) as Project[];
  },

  async findMattersByClientId(clientId: string, userId: string): Promise<Matter[]> {
    const result = await sql`
      SELECT * FROM matters
      WHERE client_id = ${clientId} AND user_id = ${userId}
      ORDER BY position ASC, created_at DESC
    `;
    return (result || []) as Matter[];
  },

  async findGoalsByClientId(clientId: string, userId: string): Promise<Goal[]> {
    const result = await sql`
      SELECT * FROM goals
      WHERE client_id = ${clientId} AND user_id = ${userId}
      ORDER BY position ASC, created_at DESC
    `;
    return (result || []) as Goal[];
  }
};

export type ClientsModule = typeof clientsModule;

export function registerClients(db: { clients?: ClientsModule }) {
  db.clients = clientsModule;
}

export { clientsModule as clients };
